"use client";
import React, { useMemo, useState, useEffect } from "react";
import Link from "next/link";
import {
  ChevronDown,
  Wallet,
  LogOut,
  Copy,
  AlertTriangle,
  Plus,
  LucideLayoutDashboard,
} from "lucide-react";
import { useSelector } from "react-redux";
import Avatar from "../../common/Avatar";

export default function MobileProfileButton({
  showProfileDropdown,
  setShowProfileDropdown,
  userProfile,
  userDataFromLocal,
  walletAddress,
  connectedWallet,
  solBalance,
  isLoadingSolBalance,
  solBalanceError,
  selectedNetworkData,
  getInitials,
  handleCopyAddress,
  setShowDisconnectWalletModal,
  setShowLogoutModal,
}) {
  const { bots = [], loading = "idle" } = useSelector(
    (state) => state.fetchBots || {},
  );
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const activeCount = useMemo(
    () => (isMounted ? bots.filter((b) => b.status === "running").length : 0),
    [bots, isMounted],
  );
  const standbyCount = useMemo(
    () => (isMounted ? bots.filter((b) => b.status === "stopped").length : 0),
    [bots, isMounted],
  );
  return (
    <div className="relative shrink-0 dropdown-container lg:hidden">
      <button
        onClick={() => setShowProfileDropdown(!showProfileDropdown)}
        className="flex shrink-0 items-center gap-1 rounded-md border border-gray-600/50 bg-gray-800/60 px-1.5 py-1 text-white transition-colors hover:border-gray-500 sm:gap-2 sm:rounded-lg sm:px-3 sm:py-2"
      >
        {/* User Avatar */}
        <Avatar
          src={userDataFromLocal?.user?.avatar || userProfile.avatar}
          name={userDataFromLocal?.user?.username || userProfile.name}
          getInitials={getInitials}
          size="sm"
          className="!h-7 !w-7 text-[10px] sm:!h-8 sm:!w-8"
        />
        <div className="text-left hidden sm:block">
          <div className="text-sm font-medium">
            {userDataFromLocal?.user?.username || userProfile.name}
          </div>
        </div>
        <ChevronDown className="h-3 w-3 text-gray-400 sm:h-3.5 sm:w-3.5" />
      </button>

      {showProfileDropdown && (
        <div className="fixed top-16 right-3 z-[110] w-[min(20rem,calc(100vw-1.5rem))] max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-lg border border-gray-700 bg-gray-900 shadow-2xl sm:absolute sm:right-0 sm:top-full sm:mt-3 sm:w-80 sm:max-w-[85vw]">
          {/* Mobile Profile Dropdown Content */}
          <div className="p-4 border-b border-gray-700/50 bg-gray-900/80">
            <div className="flex items-center space-x-3">
              <Avatar
                src={userDataFromLocal?.user?.avatar || userProfile.avatar}
                name={userDataFromLocal?.user?.username || userProfile.name}
                getInitials={getInitials}
                size="lg"
              />
              <div className="flex-1">
                <div className="text-white font-semibold">
                  {userDataFromLocal?.user?.username || userProfile.name}
                </div>
                <div className="text-gray-400 text-sm">
                  {userDataFromLocal?.user?.email || userProfile.email}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between p-2 border-b border-gray-700/50">
            <Link
              href="/"
              className="flex items-center space-x-2.5 px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl hover:bg-gray-700 hover:border-green-500 transition-colors group flex-1 mr-2"
              rel="noopener noreferrer"
            >
              <div className="w-7 h-7 bg-gray-700 rounded-lg flex items-center justify-center">
                <Plus className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-sm text-gray-200 font-semibold">
                CreateBot
              </span>
            </Link>
            <Link
              href="/dashboard"
              className="flex items-center space-x-2.5 px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl hover:bg-gray-700 hover:border-green-500 transition-colors group flex-1 ml-2"
              rel="noopener noreferrer"
            >
              <div className="w-7 h-7 bg-gray-700 rounded-lg flex items-center justify-center">
                <LucideLayoutDashboard className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-sm text-gray-200 font-semibold">
                Dashboard
              </span>
            </Link>
          </div>
          {/* Wallet Info */}
          {walletAddress && (
            <div className="p-3 border-b border-gray-700/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Connected Wallet</span>
                <button
                  onClick={handleCopyAddress}
                  className="flex items-center space-x-1 text-gray-400 hover:text-white transition-colors text-xs"
                >
                  <Copy className="w-3 h-3" />
                  <span>Copy</span>
                </button>
              </div>
              <div className="text-white font-mono text-sm mb-1">
                {walletAddress && walletAddress.length > 8
                  ? walletAddress.slice(0, 4) + "..." + walletAddress.slice(-4)
                  : walletAddress || "N/A"}
              </div>
              <div className="flex items-center justify-between">
                <div className="text-white font-semibold">
                  {solBalanceError ? (
                    <div className="flex items-center space-x-1 text-red-400">
                      <AlertTriangle className="w-3 h-3" />
                      <span className="text-sm">Error</span>
                    </div>
                  ) : solBalance !== undefined && solBalance !== null ? (
                    `${solBalance.toFixed(6)} ${
                      selectedNetworkData?.symbol || "SOL"
                    }`
                  ) : (
                    <span className="text-sm">
                      0.000000 {selectedNetworkData?.symbol || "SOL"}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-400">
                  {connectedWallet || ""}
                </div>
              </div>
            </div>
          )}

          {/* Menu Items */}
          <div className="p-1">
            {/* <button className="w-full flex items-center space-x-3 p-2.5 hover:bg-gray-700/50 rounded text-sm transition-colors">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="text-white">Edit Profile</span>
                        </button>
                        <button className="w-full flex items-center space-x-3 p-2.5 hover:bg-gray-700/50 rounded text-sm transition-colors">
                            <Settings className="w-4 h-4 text-gray-400" />
                            <span className="text-white">Account Settings</span>
                        </button> */}
            {loading === "loading" ? (
              <div className="p-4 bg-gray-900/30 rounded-lg animate-pulse">
                <div className="grid grid-cols-2 text-center gap-4">
                  <div className="flex flex-col items-center space-y-2">
                    <div className="h-6 w-10 bg-gray-700 rounded" />
                    <div className="h-3 w-20 bg-gray-700 rounded" />
                  </div>
                  <div className="flex flex-col items-center space-y-2">
                    <div className="h-6 w-10 bg-gray-700 rounded" />
                    <div className="h-3 w-20 bg-gray-700 rounded" />
                  </div>
                </div>
              </div>
            ) : isMounted ? (
              <div className="p-4 bg-gray-900/30 rounded-lg">
                <div className="grid grid-cols-2 gap-3">
                  <Link
                    href="/dashboard?tab=running"
                    className="group block rounded-lg bg-gray-800/90 border border-gray-700 hover:border-gray-600 transition-colors p-3"
                    rel="noopener noreferrer"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-300">Active bots</p>
                        <p className="text-2xl font-semibold text-white">
                          {activeCount}
                        </p>
                      </div>
                    </div>
                  </Link>

                  <Link
                    href="/dashboard?tab=standby"
                    className="group block rounded-lg bg-gray-800/90 border border-gray-700 hover:border-gray-600 transition-colors p-3"
                    rel="noopener noreferrer"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-300">Standby bots</p>
                        <p className="text-2xl font-semibold text-white">
                          {standbyCount}
                        </p>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-gray-900/30 rounded-lg">
                <div className="grid grid-cols-2 gap-3">
                  <div className="group block rounded-lg bg-gray-800/90 border border-gray-700 p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-300">Active bots</p>
                        <p className="text-2xl font-semibold text-white">0</p>
                      </div>
                    </div>
                  </div>
                  <div className="group block rounded-lg bg-gray-800/90 border border-gray-700 p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-300">Standby bots</p>
                        <p className="text-2xl font-semibold text-white">0</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="border-t border-gray-700/50 my-1" />

            {connectedWallet && (
              <button
                onClick={() => {
                  setShowDisconnectWalletModal(true);
                  setShowProfileDropdown(false);
                }}
                className="w-full flex items-center space-x-3 p-2.5 hover:bg-orange-600/20 rounded text-sm transition-colors text-orange-400"
              >
                <Wallet className="w-4 h-4" />
                <span>Disconnect Wallet</span>
              </button>
            )}
            <button
              onClick={() => {
                setShowLogoutModal(true);
                setShowProfileDropdown(false);
              }}
              className="w-full mt-2 flex items-center space-x-3 p-2.5 hover:bg-red-600/20 rounded text-sm transition-colors text-red-400"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
